import { useEffect, useRef, useState, useCallback } from "react";
import { fetchNotificationBadges, type NotificationBadges } from "../api/client";

/**
 * US-44: hook для polling `/api/notifications/badges/`.
 *
 * - При маунте — сразу загружает счётчики.
 * - Polling каждые `intervalMs` (по умолчанию 30s).
 * - Также рефрешит при возврате фокуса на окно (visibilitychange) —
 *   когда пользователь возвращается после переключения вкладок, видит
 *   актуальные числа без 30s задержки.
 * - Игнорирует ошибки (если эндпоинт временно недоступен — оставляем
 *   старые значения, не рушим UI).
 */
export function useNotificationBadges(intervalMs = 30_000): {
  badges: NotificationBadges | null;
  refresh: () => Promise<void>;
} {
  const [badges, setBadges] = useState<NotificationBadges | null>(null);
  // Защита от двойного полла при unmount/remount.
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchNotificationBadges();
      if (mountedRef.current) {
        setBadges(data);
      }
    } catch {
      // Сетевая ошибка / 401 после logout — молча.
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    const id = setInterval(() => {
      void refresh();
    }, intervalMs);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refresh, intervalMs]);

  return { badges, refresh };
}

/**
 * Хелперы для агрегирования счётчиков под пункты меню SPA.
 *
 * Контекст: в навигации сейчас один пункт «На согласовании» (LR+PM+HR-очереди
 * в одном экране с tabs), отдельные пункты «Таймшит» и «Отсутствия» для
 * собственных недель/абсансов сотрудника.
 *
 * - approval: всё actionable в очередях (LR ts + PM ts + LR/PM/HR abs).
 * - timesheet:  мои таймшит-недели, требующие действия (rework / rolled back).
 * - absence: мои абсансы (draft + returned).
 *
 * Решение из backlog US-44: бейджи объединяют «очередь + своё» одной цифрой
 * для каждого пункта меню. Очереди — на пункте «На согласовании».
 */

export function approvalBadgeCount(b: NotificationBadges | null): number {
  if (!b) return 0;
  return (
    b.lr_timesheet_queue +
    b.pm_timesheet_queue +
    b.lr_absence_queue +
    // US-107: ступень РП в согласовании отсутствий — без неё руководитель
    // проекта не увидел бы, что от него ждут решения.
    (b.pm_absence_queue ?? 0) +
    b.hr_absence_queue
  );
}

export function timesheetBadgeCount(b: NotificationBadges | null): number {
  return b?.my_timesheet_action_required ?? 0;
}

export function absenceBadgeCount(b: NotificationBadges | null): number {
  return b?.my_absence_action_required ?? 0;
}

// US-44: суб-табы в "На согласовании".
export function approvalTimesheetTabBadge(b: NotificationBadges | null): number {
  if (!b) return 0;
  return b.lr_timesheet_queue + b.pm_timesheet_queue;
}

export function approvalAbsenceTabBadge(b: NotificationBadges | null): number {
  if (!b) return 0;
  return b.lr_absence_queue + (b.pm_absence_queue ?? 0) + b.hr_absence_queue;
}
